import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Container, Paper, Typography, TextField, Button, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpOnlySecurityService } from '../../application/iam';

interface LoginFormInputs {
  username: string;
  password: string;
}

const schema = yup.object({
  username: yup.string().required('auth.usernameRequired'),
  password: yup.string().required('auth.passwordRequired'),
}).required();

const Login: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormInputs>({
    resolver: yupResolver(schema)
  });

  const onSubmit = async (data: LoginFormInputs) => {
    try {
      // Implement SSO login using httpOnlySecurityService
      httpOnlySecurityService?.login(from);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  return (
    <Container maxWidth="lg" className="py-8" data-testid="login-container">
      <div className="mb-8" data-testid="login-description">
        <Typography variant="h4" component="h1" className="text-[#CC0032] mb-4" data-testid="login-title">
          iPep Version 12.0.0
        </Typography>
        <Typography variant="body1" className="text-[#003140] mb-4">
          iPep Version 12.0.0 allows for easier and more sophisticated exchange of Interconnect and UPU accounting data via the web. Currently, modules for the processing of sampling information (eNO1 and e53), mail volume data (e55) and IBRS item information (e09) are available with the following features:
        </Typography>
        <ul className="list-disc pl-8 space-y-2 text-[#003140] mb-8">
          <li>Data validation to ensure high formal data quality</li>
          <li>Upload and download of data files for collaborative updating of accounting information by the inbound and outbound DO</li>
          <li>Amendments of records and resubmission of accounting files</li>
          <li>Exchange of supportive information for single records</li>
          <li>Formal agreement of accounting data</li>
          <li>E-mail notification of upload, download, comments, (request for) removal and/or acceptance</li>
          <li>Compact overview of accounting status</li>
          <li>Accuracy reports for sampling data based on continuous sampling</li>
          <li>Sampling Planning Module for the forecast of stratum sizes and the calculation of reliable sampling rates</li>
          <li>Comparison reports of sampling and volume information to validate the item estimation</li>
          <li>eNO1 Playground to simulate and analyze different sampling or accounting scenarios</li>
          <li>Recapitulatory reports for sampling information (NO2/CN54), mail volume data (CN56) and IBRS item information (CN10) to facilitate and speed up the accounting process</li>
          <li>Generation of accounting documents (NO3/variant of CN54bis) with possibility to upload/download signed forms</li>
          <li>Generation of accounting documents for format C postcards (NO3)</li>
          <li>Generation of IBRS accounting documents (CN19) with possibility to upload/download IBRS rates</li>
          <li>Overview of all accounting relevant NO3-, CN56- and CN10-figures and input of bilateral values</li>
          <li>Definition of Remuneration Groups (RG) on the basis of IMPC codes with different Terminal Dues rates</li>
          <li>Calculation of compound rates for Interconnect accounting by the generation of the Compound Rate Report (CRR)</li>
          <li>Generation of continuative accounting documents (NO4, CN61 Interconnect/UPU) including a complete summary of accounting relevant volumes and Terminal Dues rates</li>
          <li>User-specific settings and user administration</li>
        </ul>
      </div>

      <Paper elevation={3} className="p-8 max-w-sm mx-auto" data-testid="login-form-paper">
        <Typography variant="h5" component="h2" gutterBottom align="center" data-testid="login-form-title">
          {t('auth.login')}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="login-form">
          <TextField
            {...register('username')}
            label={t('auth.username')}
            fullWidth
            error={!!errors.username}
            helperText={errors.username ? t(errors.username.message!) : ''}
            data-testid="login-username-field"
          />
          <TextField
            {...register('password')}
            label={t('auth.password')}
            type="password"
            fullWidth
            error={!!errors.password}
            helperText={errors.password ? t(errors.password.message!) : ''}
            data-testid="login-password-field"
          />
          <Box className="pt-4">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              data-testid="login-submit-button"
            >
              {t('auth.login')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default Login; 